// --- START PATCH: Responsive, accessible Navbar (brand centered, shadcn sheet for mobile, pill active links, cart badge) ---
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/lib/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/lib/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/ui/popover";
import { useCart } from "@/features/cart/hooks/useCart";
import AccountMenu from "@/features/auth/components/AccountMenu";
import { Menu, ShoppingCart, Globe } from "lucide-react";
import { useState } from "react";
import clsx from "clsx";
import { useAuth } from "@/auth/useAuth";
import { useTranslation } from "react-i18next";

// pill sizing for desktop links
function pillClasses(isActive: boolean) {
  return clsx(
    "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1.5 text-[14px] font-medium transition-colors",
    "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    isActive ? "bg-primary text-primary-foreground" : "bg-transparent"
  );
}

// Brand component: tries to load /logo.svg or /logo.png and falls back to text
function Brand() {
  return (
    <Link
      to="/"
      className="group inline-flex items-center gap-2"
      aria-label="DM 3D Printing - Home"
    >
      <picture>
        <source srcSet="/logo.svg" type="image/svg+xml" />
        <img
          src="/logo.png"
          alt="DM 3D Printing logo"
          className="h-8 w-auto rounded-sm object-contain"
          loading="eager"
          decoding="async"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      </picture>
      <span className="text-[17px] font-semibold tracking-tight group-hover:opacity-90">
        DM 3D Printing
      </span>
    </Link>
  );
}

// --- START: Compact language selector component (pill with globe icon + EN/PT) ---
function LanguageSelector() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  
  // Helper to check if current language is Portuguese
  // Handles both 'pt' and 'pt-PT' variants for robustness
  const isPortuguese = (lang: string) => lang === 'pt-PT' || lang === 'pt';
  
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setOpen(false); // Close popover after selection
  };
  
  // Get current language initials (PT or EN) based on resolved language
  const currentLang = isPortuguese(i18n.resolvedLanguage || '') ? 'PT' : 'EN';
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select language"
          className={clsx(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition-colors",
            "border border-border/50",
            "hover:bg-accent hover:text-accent-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
        >
          <Globe className="h-3.5 w-3.5 opacity-70" />
          <span>{currentLang}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 p-2">
        <div className="flex flex-col gap-1">
          <button
            onClick={() => changeLanguage('pt-PT')}
            className={clsx(
              "w-full text-left rounded-md px-3 py-2 text-sm transition-colors",
              isPortuguese(i18n.resolvedLanguage || '')
                ? "bg-primary text-primary-foreground font-medium"
                : "hover:bg-accent hover:text-accent-foreground"
            )}
          >
            Português (PT)
          </button>
          <button
            onClick={() => changeLanguage('en')}
            className={clsx(
              "w-full text-left rounded-md px-3 py-2 text-sm transition-colors",
              // English only has one variant, so direct comparison is sufficient
              i18n.resolvedLanguage === 'en'
                ? "bg-primary text-primary-foreground font-medium"
                : "hover:bg-accent hover:text-accent-foreground"
            )}
          >
            English
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
// --- END: Compact language selector component (pill with globe icon + EN/PT) ---

const Navbar = () => {
  const { t } = useTranslation('nav');
  const { count } = useCart();
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  const [open, setOpen] = useState(false);

  // --- START EDIT: Nav items now use translations ---
  const NAV_ITEMS = [
    { to: "/", label: t('home') },
    { to: "/portfolio", label: t('portfolio') },
    { to: "/products", label: t('products') },
    { to: "/filaments", label: t('filaments') },
    { to: "/material-guide", label: t('materialGuide') },
    { to: "/contact", label: t('contact') },
  ];
  // --- END EDIT: Nav items now use translations ---

  return (
    <header
      className={[
        "sticky top-0 z-50 border-b border-border/50",
        "bg-background/80 supports-[backdrop-filter]:bg-background/60 backdrop-blur",
      ].join(" ")}
      role="banner"
    >
      {/* Skip to content for accessibility */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
      >
        {t('skipToContent')}
      </a>

      <nav
        className="relative mx-auto flex h-16 w-full max-w-7xl items-center px-4 md:px-6"
        role="navigation"
        aria-label="Main"
      >
        {/* Left: desktop nav or mobile menu button */}
        <div className="flex items-center">
          {/* Mobile hamburger */}
          <div className="md:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t('openMenu')}>
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">{t('openMenu')}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[84vw] sm:w-96">
                <SheetHeader className="mb-4">
                  <SheetTitle>{t('menu')}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-2">
                  {NAV_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        clsx(
                          "w-full text-left",
                          "rounded-full px-3 py-2 text-base font-medium",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-accent"
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}

                  {/* CTA: Upload & Print (mobile) */}
                  {/* --- START EDIT: make CTA outline to avoid looking pre-selected --- */}
                  {!isAdmin && (
                    <Link to="/upload" onClick={() => setOpen(false)}>
                      <Button
                        variant="outline"
                        className="w-full rounded-full"
                        size="lg"
                      >
                        {t('uploadAndPrint')}
                      </Button>
                    </Link>
                  )}
                  {/* --- END EDIT --- */}

                  {/* --- START EDIT: Add language selector and Account to mobile sheet --- */}
                  <div className="mt-4 flex flex-col gap-3">
                    <LanguageSelector />
                    <AccountMenu />
                  </div>
                  {/* --- END EDIT --- */}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop links */}
          <ul className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) => pillClasses(isActive)}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Center: Brand – absolutely centered within the nav bar so it stays
            perfectly centered regardless of how many items the left nav has.
            pointer-events-none on the overlay lets clicks pass through to nav
            links behind it; pointer-events-auto restores interactivity on the
            Brand link itself. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="pointer-events-auto">
            <Brand />
          </div>
        </div>

        {/* Right: actions */}
        <div className="ml-auto flex items-center gap-2">
          {/* CTA on desktop */}
          {!isAdmin && (
            <div className="hidden md:block">
              {/* --- START EDIT: make CTA outline to avoid looking pre-selected --- */}
              <Link to="/upload">
                <Button variant="outline" className="rounded-full">
                  {t('uploadAndPrint')}
                </Button>
              </Link>
              {/* --- END EDIT --- */}
            </div>
          )}

          {/* Cart with numeric badge */}
          {/* --- START EDIT: ensure icon remains visible by inverting text on light hover --- */}
          <Link
            to="/cart"
            aria-label={`${t('cart')}${
              count > 0 ? `, ${count} item${count === 1 ? "" : "s"}` : ""
            }`}
            className={clsx(
              "relative inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors",
              "border border-border",
              "text-foreground", // base: visible on dark navbar
              "hover:bg-primary hover:text-primary-foreground", // hover: light pill and dark icon (like navbar buttons)
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span
                className={clsx(
                  "absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-primary px-1.5 text-center text-[11px] font-semibold leading-5 text-primary-foreground",
                  "shadow-sm"
                )}
                aria-label={`${count} item${count === 1 ? "" : "s"} in cart`}
              >
                {count}
              </span>
            )}
          </Link>

          {/* --- START EDIT: Add language selector and Account on desktop --- */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSelector />
            <AccountMenu />
          </div>
          {/* --- END EDIT --- */}
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
// --- END PATCH: Responsive, accessible Navbar (brand centered, shadcn sheet for mobile, pill active links, cart badge) ---
